import { ComponentProps, FC } from 'react';
import {
  ImageList as MuiImageList,
  ImageListItem,
  Skeleton,
  ImageListItemBar,
  Stack,
} from '@mui/material';
import { useFiles } from 'store/files';
import { SystemColors } from 'melp-design/style';
import { Button } from 'melp-design/components';

interface BaseImageListProps {
  items: Array<{
    src: string;
    name?: string;
    actions?: Array<ComponentProps<typeof Button>>;
    state?: 'neutral' | 'active' | 'faded';
  }>;
  isLoading?: boolean;
  imageFit?: 'cover' | 'contain' | 'full';
  cols?: number;
  aspectRatio?: [number, number];
}

export const BaseImageList: FC<BaseImageListProps> = ({
  items,
  imageFit = 'contain',
  isLoading = false,
  cols = 2,
  aspectRatio = [16, 9],
}) => {
  return (
    <MuiImageList cols={cols} gap={10} sx={{ my: 1 }}>
      {items.map((item) => {
        return (
          <ImageListItem
            onMouseEnter={() => {}}
            sx={{
              border:
                item.state === 'active'
                  ? `1px solid ${SystemColors.primary.base}`
                  : item.state === 'neutral'
                  ? `1px dashed ${SystemColors.grey[55]}`
                  : `1px dashed ${SystemColors.grey[55]}`,
              borderRadius: 1.5,
              overflow: 'hidden',
              opacity: item.state === 'faded' ? 0.8 : 1,
            }}
            key={item.src}
          >
            {item.actions ? (
              <ImageListItemBar
                position="top"
                actionIcon={
                  <Stack flexDirection="row" flexWrap="wrap" gap={1}>
                    {item.actions.map((action, i) => (
                      <Button {...action} size="sm" key={i} />
                    ))}
                  </Stack>
                }
                sx={{
                  p: 1,
                  m: '1px',
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  background: 'rgba(0, 0, 0, 0.25)',
                }}
              />
            ) : null}
            <img
              src={item.src}
              alt={item.name ?? ''}
              style={{
                aspectRatio:
                  imageFit === 'full' ? undefined : aspectRatio.join('/'),
                objectFit: imageFit === 'full' ? 'fill' : imageFit,
              }}
            />
          </ImageListItem>
        );
      })}
      {isLoading
        ? Array.from({ length: items.length }).map((_, i) => (
            <ImageListItem key={i}>
              <Skeleton
                sx={{ aspectRatio: aspectRatio.join('/') }}
                height="100%"
                variant="rectangular"
              />
            </ImageListItem>
          ))
        : null}
    </MuiImageList>
  );
};

type Props = {
  items: Array<{
    imageId: string;
    actions?: Array<ComponentProps<typeof Button>>;
  }>;
} & Omit<BaseImageListProps, 'items' | 'isLoading'>;

export const ImageList: FC<Props> = ({ items, ...baseImageListProps }) => {
  const { data: images = [], isLoading } = useFiles(
    items.map((item) => item.imageId),
  );

  return (
    <BaseImageList
      items={items
        .map((item) => {
          const image = images.find((image) => image.id === item.imageId);

          if (!image) return null;

          return {
            src: image.url,
            name: image.name,
            actions: item.actions,
          };
        })
        .filter((item) => item !== null)}
      isLoading={isLoading}
      {...baseImageListProps}
    />
  );
};
